// @flow

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import type { State } from "@store/reducers";
import actionCreators from "@modules/reporting/actionCreator";
import { getReportsSchema } from "@modules/reporting/selectors/trends/absence-rate";
import type { AddEditModalConfig } from "@modules/reporting/reducer/modals/types";
import { ReportModalStepTypes } from "@modules/reporting/constants/reports";

import type {
  DefaultReport,
  MySavedReport,
  AddEditReportModalType,
  SaveReportModalType,
} from "@modules/reporting/reducer/modals/types";
import { getAddEditReportModal, getSaveReportModal } from "@modules/reporting/selectors/modals";
import { split } from "@transformers/schema";
import AddEditReportModal from "../add-edit-report-modal";
import SaveReportModal from "../save-report-modal";
import StepFiltersView from "./step-filters-view";
import AbsenceRateTrendsSchema from "../../transformers/AbsenceRateTrendsSchema";
import * as sharedSelectors from "@modules/shared/selectors";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";
import config from "@config";
import type { ServiceSettings } from "@modules/service/reducer/service-settings/types";
import { getServiceSettings } from "@modules/settings/selectors";

type OwnProps = $Exact<{
  clientId: number,
  returnReportToSave?: (data: MySavedReport) => void,
  getReport?: (data: MySavedReport) => () => void,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  addEditModal: AddEditReportModalType,
  saveReportModal: SaveReportModalType,
  reasons: Array<AbsenceReason>,
  serviceSettings: ServiceSettings,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceRateTrendsReportSchema: typeof actionCreators.GetAbsenceRateTrendsReportSchema,
  createReport: typeof actionCreators.CreateAbsenceRateTrendsReport,
  updateReport: typeof actionCreators.UpdateAbsenceRateTrendsReport,
  setReportToSave: typeof actionCreators.SetAbsenceRateTrendsReportToSave,
  getReasons: typeof sharedActionCreators.GetAbsenceReasons,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

type ComponentState = $Exact<{
  data: Object,
}>;

export function AbsenceRateTrendsAddEditReportModal(props: Props) {
  const [data, setData] = useState({});

  const enableBV12 = props?.serviceSettings?.reportingParameters?.bv12Trend;
  const schema = props.schema ? AbsenceRateTrendsSchema(props.schema, props.t, enableBV12) : {};

  useEffect(() => {
    props.getAbsenceRateTrendsReportSchema({
      clientId: props.clientId,
    });
    props.getReasons({ clientId: props.clientId, activeOnly: false });
  }, []);

  function onUpdateReport(data: MySavedReport) {
    if (props.returnReportToSave) {
      props.returnReportToSave(data);
      props.toggleAddEditReportModal({ visible: false });
      return;
    }

    props.updateReport({
      clientId: props.clientId,
      reportId: data.id,
      data,
    });
    props.toggleAddEditReportModal({ visible: false });
  }

  function onCreateReport(data: DefaultReport) {
    props.createReport({
      clientId: props.clientId,
      ...data,
    });
  }

  function onGetReport(data: MySavedReport) {
    if (!props.getReport) {
      return;
    }

    props.getReport(data)();
    props.setReportToSave({
      ...data,
      isNew: true,
    });
    props.toggleAddEditReportModal({ visible: false });
  }

  function getFiltersView(data: Object) {
    return <StepFiltersView data={data} />;
  }

  function stepFiltersUiSchema(t: Function, config: Object) {
    return {
      staffGroups: {
        items: {
          id: {
            "ui:options": {
              hidelabel: true,
            },
          },
        },
        "ui:field": "multi-select",
      },
      excludeAbsenceReasons: {
        "ui:header": isEditModal() && t("REASON"),
        "ui:title": t("EXCLUDED_REASONS"),
        items: {
          id: {
            "ui:options": {
              hidelabel: true,
            },
          },
        },
        "ui:field": "multi-select",
      },
      comparator: {
        "ui:widget": "hidden",
      },
      fromDate: {
        "ui:title": t("FROM_DATE"),
        "ui:widget": "date-picker",
        "ui:options": { dateFormat: config.dateFormatBE },
      },
      toDate: {
        "ui:title": t("TO_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
        },
      },
      underreportingAdjustmentApplied: {
        "ui:widget": "select",
      },
      dateRange: {
        "ui:header": isEditModal() && t("PERIOD"),
        "ui:options": {
          tooltip: {
            title: t("REPORT_FILTER_DATE_RANGE"),
            description: t("DATE_RANGE_TOOLTIP"),
            id: "trend-absence-rate-report-date-range",
            size: "medium",
          },
        },
      },
      absenceType: {
        "ui:header": isEditModal() && t("STATUS"),
      },
      absenceLength: { "ui:header": isEditModal() && t("LENGTH") },
      employeeGender: { "ui:header": isEditModal() && t("EMPLOYEES") },
      "ui:order": isEditModal()
        ? [
            //PERIOD
            "dateRange",
            "fromDate",
            "toDate",
            //STATUS
            "absenceType",
            "trendType",
            "periodType",
            "groupBy",
            "workRelated",
            //LENGTH
            "absenceLength",
            //REASON
            "excludeAbsenceReasons",
            //EMPLOYEES
            "employeeGender",
            "staffGroups",
            "comparator",
          ]
        : [
            "dateRange",
            "periodType",
            "fromDate",
            "employeeGender",
            "toDate",
            "absenceLength",
            "absenceType",
            "staffGroups",
            "trendType",
            "excludeAbsenceReasons",
            "*",
          ],
    };
  }
  function filtersSchema() {
    if (!schema || !schema?.properties?.excludeAbsenceReasons) {
      return {};
    }

    const _schema = split(schema, [
      "dateRange",
      "fromDate",
      "toDate",
      "trendType",
      "absenceType",
      "staffGroups",
      "absenceLength",
      "ageGroup",
      "employeeGender",
      "periodType",
      "underreportingAdjustmentApplied",
      "comparator",
      "groupBy",
      "workRelated",
      "excludeAbsenceReasons",
    ]);

    schema.properties.excludeAbsenceReasons.items.properties.id.oneOf = props.reasons.map(
      (reason: AbsenceReason) => {
        return {
          const: reason.id,
          title: reason.isActive ? `${reason.label}` : `${reason.label} - Inactive`,
          optionClass: !reason.isActive && `text-color-red`,
        };
      },
    );

    return _schema;
  }

  function modalConfig(): AddEditModalConfig {
    return [
      {
        type: ReportModalStepTypes.FILTERS,
        title: {
          new: props.t("SET_FILTERS"),
          edit: props.t("FILTERS"),
        },
        schema: filtersSchema(),
        uiSchema: stepFiltersUiSchema(props.t, config),
        getView: getFiltersView,
        hideNextButton: true,
      },
    ];
  }
  function isEditModal() {
    return !!props.addEditModal.data;
  }
  return (
    <React.Fragment>
      {props.addEditModal.visible && (
        <AddEditReportModal
          clientId={props.clientId}
          initialStep={props.addEditModal.initialStep}
          getReport={onGetReport}
          updateReport={onUpdateReport}
          modalConfig={modalConfig()}
          onChange={(data: Object) => {
            setData(data);
          }}
        />
      )}
      {props.saveReportModal.visible && (
        <SaveReportModal createReport={onCreateReport} updateReport={onUpdateReport} />
      )}
    </React.Fragment>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  schema: getReportsSchema(state),
  addEditModal: getAddEditReportModal(state),
  saveReportModal: getSaveReportModal(state),
  reasons: sharedSelectors.getAbsenceReasons(state), // ABSENCE REASONS
  serviceSettings: getServiceSettings(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceRateTrendsReportSchema: actionCreators.GetAbsenceRateTrendsReportSchema,
  createReport: actionCreators.CreateAbsenceRateTrendsReport,
  updateReport: actionCreators.UpdateAbsenceRateTrendsReport,
  setReportToSave: actionCreators.SetAbsenceRateTrendsReportToSave,
  getReasons: sharedActionCreators.GetAbsenceReasons, // ABSENCE REASONS
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AbsenceRateTrendsAddEditReportModal);
